/**
 * Režim práce — čo technik práve na stĺpoch robí.
 *
 * Pôvodne to bol prepínač „Admin / Montážnik" len pre admina, aby videl, čo
 * vidí Kristian. Lenže je to v skutočnosti to isté rozhodnutie, aké potrebuje
 * aj samotný technik: idem zvárať, alebo idem pripájať zákazníkov? Preto je
 * tu jeden stav namiesto dvoch konkurenčných prepínačov.
 *
 *   zvaranie   → klik na stĺp otvorí kazetu, zvary a splittre (technická správa)
 *   pripajanie → klik na stĺp otvorí voľné porty a zákazníkov (montážny dialóg)
 *
 * Kto ktorý režim smie, sa odvodí z modulov (useRezimPrace nižšie), nie z role —
 * backend ich od 5. 10. 2026 vynucuje, takže ponúkať režim bez modulu by
 * znamenalo poslať človeka rovno do 403.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from '../stores/authStore';

export type RezimPrace = 'zvaranie' | 'pripajanie';

/** Staršie pomenovanie, ponechané kvôli existujúcim volaniam. */
export type RoleViewMode = 'admin' | 'install_tech';

interface RoleViewState {
  /** null = používateľ si zatiaľ nevybral, platí predvolba podľa role a modulov. */
  rezimPrace: RezimPrace | null;
  setRezimPrace: (rezim: RezimPrace) => void;

  /** Starý názov — 'install_tech' zodpovedá režimu pripájania. */
  roleViewMode: RoleViewMode;
  setRoleViewMode: (mode: RoleViewMode) => void;
  isInstallTechView: () => boolean;
}

export const useRoleViewStore = create<RoleViewState>()(
  persist(
    (set, get) => ({
      rezimPrace: null,

      setRezimPrace: (rezim: RezimPrace) => {
        set({
          rezimPrace: rezim,
          roleViewMode: rezim === 'pripajanie' ? 'install_tech' : 'admin',
        });
      },

      roleViewMode: 'admin',

      setRoleViewMode: (mode: RoleViewMode) => {
        set({
          roleViewMode: mode,
          rezimPrace: mode === 'install_tech' ? 'pripajanie' : 'zvaranie',
        });
      },

      isInstallTechView: () => get().rezimPrace === 'pripajanie',
    }),
    {
      name: 'role-view-storage',
    }
  )
);

/**
 * Ktorý režim komu patrí. Čistá funkcia, aby sa dala otestovať bez prehliadača.
 *
 * Režim sa dá prepnúť len na ten, na ktorý má človek modul s úrovňou Úprava.
 * Kto má jediný z nich, dostane rovno ten svoj a žiadnu zbytočnú voľbu; kto
 * nemá ani jeden (napríklad pozorovateľ), zostane na zváracom — to je bežný
 * pohľad na stĺp, aký mal doteraz.
 *
 * Pozor na poradie nasadzovania: kým backend neposiela úrovne modulov, vyjde
 * `mozeZvarat` aj `mozePripajat` na false všetkým okrem admina. Preto sa na
 * `jePripajanie` nikde nespoliehame ako na jediný zdroj — montážnik sa pozná
 * aj podľa role.
 */
export const odvodRezim = (vstup: {
  ulozeny: RezimPrace | null;
  mozeZvarat: boolean;
  mozePripajat: boolean;
  rola?: string;
}): { rezim: RezimPrace; maPrepinac: boolean } => {
  const { ulozeny, mozeZvarat, mozePripajat, rola } = vstup;

  // Montážnik chodí pripájať — nech ho prvé spustenie nehodí do zvárania.
  const predvolene: RezimPrace =
    mozePripajat && (rola === 'install_tech' || !mozeZvarat)
      ? 'pripajanie'
      : 'zvaranie';

  const dostupny = (r: RezimPrace) => (r === 'zvaranie' ? mozeZvarat : mozePripajat);

  return {
    rezim: ulozeny && dostupny(ulozeny) ? ulozeny : predvolene,
    // Prepínač má zmysel len vtedy, keď je naozaj z čoho vyberať.
    maPrepinac: mozeZvarat && mozePripajat,
  };
};

/** Účinný režim práce a to, či sa má vôbec ponúkať prepínač. */
export const useRezimPrace = () => {
  const ulozeny = useRoleViewStore((s) => s.rezimPrace);
  const setRezimPrace = useRoleViewStore((s) => s.setRezimPrace);
  const hasModule = useAuthStore((s) => s.hasModule);
  const user = useAuthStore((s) => s.user);

  const mozeZvarat = hasModule('cassette_editor', 'edit');
  const mozePripajat = hasModule('installation_checklist', 'edit');

  const { rezim, maPrepinac } = odvodRezim({
    ulozeny,
    mozeZvarat,
    mozePripajat,
    rola: user?.role,
  });

  return {
    rezim,
    nastavRezim: setRezimPrace,
    mozeZvarat,
    mozePripajat,
    maPrepinac,
    jePripajanie: rezim === 'pripajanie',
  };
};
